import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { TrackLoop, } from '@livekit/components-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from '@marvelapp/ballpark-icons';
import theme from '@marvelapp/ballpark-theme';
import { BtwButton } from '../BtwButton';
import { BtwText } from '../BtwText';
import { Stack } from '../Stack';
import { ParticipantTile } from './ParticipantTile';
function getGridLayout(container, totalItems) {
    // Extract container dimensions, accounting for padding
    const containerWidth = container.clientWidth - 64;
    const containerHeight = container.clientHeight - 64;
    // Constants
    const maxItemSize = 400;
    const minItemSize = 300;
    const maxRows = 3;
    const gap = 40;
    // Calculate maximum possible columns based on minimum item size
    const maxPossibleColumns = Math.floor((containerWidth + gap) / (minItemSize + gap));
    // Start with maximum possible columns, but no more than total items
    let columns = Math.min(maxPossibleColumns, totalItems);
    // Initialize variables for best configuration
    let bestConfig = {
        columns: 0,
        rows: 0,
        itemDimension: 0,
        maxItemsPerPage: 0,
    };
    // Try each column configuration from max down to 1
    while (columns > 0) {
        // Calculate item dimension for current column count
        const itemDimension = Math.min(maxItemSize, (containerWidth - (columns - 1) * gap) / columns);
        // Skip if items would be too small
        if (itemDimension < minItemSize) {
            columns--;
        }
        // Calculate how many rows we need for this column count
        const neededRows = Math.ceil(totalItems / columns);
        // Calculate maximum possible rows we can fit
        const maxPossibleRows = Math.floor((containerHeight + gap) / (itemDimension + gap));
        // Determine actual rows we'll use
        const rows = Math.min(maxRows, maxPossibleRows, neededRows);
        // Calculate how many items we can fit with this configuration
        const itemsPerPage = columns * rows;
        // Update best configuration if:
        // 1. This configuration can fit more items, OR
        // 2. This configuration can fit the same number of items but uses fewer rows
        if (itemsPerPage > bestConfig.maxItemsPerPage ||
            (itemsPerPage === bestConfig.maxItemsPerPage && rows < bestConfig.rows)) {
            bestConfig = {
                columns,
                rows,
                itemDimension,
                maxItemsPerPage: itemsPerPage,
            };
            // If we can fit all items in a single row, we're done
            if (itemsPerPage >= totalItems && rows === 1) {
                break;
            }
        }
        columns--;
    }
    return bestConfig;
}
function useTracksPagination(tracks, maxItemsPerPage, setDirection) {
    const [currentPage, setCurrentPage] = useState(0);
    // Calculate total pages needed
    const totalPages = Math.ceil(tracks.length / maxItemsPerPage);
    // Navigation functions
    const goToNextPage = useCallback(() => {
        setDirection('next');
        setCurrentPage((prev) => (prev + 1) % totalPages);
    }, [totalPages, setDirection]);
    const goToPreviousPage = useCallback(() => {
        setDirection('prev');
        setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
    }, [totalPages, setDirection]);
    // Ensure current page is valid whenever maxItemsPerPage changes
    useEffect(() => {
        if (currentPage >= totalPages) {
            setCurrentPage(Math.max(0, totalPages - 1));
        }
    }, [currentPage, totalPages, maxItemsPerPage]);
    // Get current visible tracks
    const visibleTracks = useMemo(() => {
        const start = currentPage * maxItemsPerPage;
        const end = start + maxItemsPerPage;
        return tracks.slice(start, end);
    }, [tracks, currentPage, maxItemsPerPage]);
    return {
        currentPage,
        totalPages,
        visibleTracks,
        goToNextPage,
        goToPreviousPage,
    };
}
const slideAnimationVariants = {
    initial: (direction) => ({
        x: direction === 'next' ? '100%' : '-100%',
        opacity: 0,
    }),
    animate: {
        x: 0,
        opacity: 1,
    },
    exit: (direction) => ({
        x: direction === 'next' ? '-100%' : '100%',
        opacity: 0,
    }),
};
const transition = {
    duration: 0.3,
    ease: theme.easings.smooth.array,
};
export const ParticipantGrid = observer(function ParticipantGrid(props) {
    var _a;
    const { tracks } = props;
    const containerRef = useRef(null);
    const [gridLayout, setGridLayout] = useState(null);
    const [direction, setDirection] = useState('next');
    const shouldReduceMotion = useReducedMotion();
    useEffect(() => {
        const updateLayout = () => {
            if (containerRef.current) {
                const layout = getGridLayout(containerRef.current, tracks.length);
                setGridLayout(layout);
            }
        };
        updateLayout();
        window.addEventListener('resize', updateLayout);
        return () => window.removeEventListener('resize', updateLayout);
    }, [containerRef, tracks]);
    const { visibleTracks, currentPage, totalPages, goToNextPage, goToPreviousPage, } = useTracksPagination(tracks, (_a = gridLayout === null || gridLayout === void 0 ? void 0 : gridLayout.maxItemsPerPage) !== null && _a !== void 0 ? _a : tracks.length, setDirection);
    return (_jsxs("div", { className: "relative h-full w-full", ref: containerRef, children: [_jsx(AnimatePresence, { custom: direction, children: _jsx(motion.div, { custom: direction, variants: !shouldReduceMotion ? slideAnimationVariants : undefined, initial: !totalPages || totalPages <= 1 ? false : 'initial', animate: "animate", exit: "exit", transition: transition, className: "absolute inset-0 h-full w-full", children: _jsx("div", { className: "lk-speakers-grid relative h-full w-full", style: {
                            '--grid-columns': gridLayout === null || gridLayout === void 0 ? void 0 : gridLayout.columns,
                            '--grid-rows': gridLayout === null || gridLayout === void 0 ? void 0 : gridLayout.rows,
                            '--item-dimension': gridLayout
                                ? `${gridLayout.itemDimension}px`
                                : undefined,
                        }, children: _jsx(TrackLoop, { tracks: visibleTracks, children: _jsx(ParticipantTile, {}) }) }) }, currentPage) }), totalPages > 1 && (_jsxs(Stack, { direction: "row", className: "absolute inset-x-0 bottom-2 items-center justify-center", gap: "2", children: [_jsx(BtwButton, { standaloneIcon: _jsx(ArrowLeftIcon, {}), onClick: goToPreviousPage, variant: "ghost", size: "sm" }), _jsxs(BtwText, { size: "sm", weight: "medium", children: [currentPage + 1, " / ", totalPages] }), _jsx(BtwButton, { standaloneIcon: _jsx(ArrowRightIcon, {}), onClick: goToNextPage, variant: "ghost", size: "sm" })] }))] }));
});
